import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { Popover } from '@mui/material';
import { produce } from 'immer';
import icons from '@bbkAdminUtils/icons';
import { CollapsableForm } from '@bbkAdminComponents/play/play-utils';
import OfferPresentationMode, {
  OfferPresentationModeDropdown,
} from '@bbkAdminComponents/play/offer-presentation-mode';
import {
  ADD_OFFER_VALUE,
  PlayContext,
  PlayObjectProperty,
  PlayValidationErrorType,
} from '@bbkAdminComponents/play/play-context';
import GenericSelect from '@bbkAdminComponents/select/select';
import { offersAndLossAversionsSlice } from '@bbkAdminRedux/rtkq/offers-and-lossAversions.slice';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import { RoutingPercentEnum } from '@bbkAdminUtils/api-client/rest-routing-api';
import { type PercentToTargetType } from '@bbkAdminUtils/api-client/routing.types';
import { generateNewIdInt } from '@bbkAdminComponents/experiences/offers/utils';
import {
  appPersistSlice,
  selectAppMisc,
} from '@bbkAdminRedux/rtkq/app-persist/app-persist.slice';
import { useAppDispatch } from '@bbkAdminRedux/redux_utils';
import { OfferNodeType } from '@bbkAdminComponents/play/play-offer-node-select';
import { AddControlButton } from '@bbkAdminComponents/play/add-control-button';
import { PlayControlSelect } from '@bbkAdminComponents/play/play-control-select';
import { DisableContainer } from '@bbkAdminComponents/play/disable-container';
import {
  type Action,
  GenericActionMenu,
} from '@bbkAdminComponents/action-menu/generic-action-menu';
import { ActionMenuIcons } from '@bbkAdminComponents/action-menu/action-menu-icons';
import { BBKRequestContext } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { TippyTooltip } from '@bbkAdminComponents/tooltips/tippy-tooltip';
import { useEnhancedOfferSelect } from '@bbkAdminComponents/play/offer-components/enhanced-offer-select';
import { useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import { FeatureFlagsE } from '@bbkAdminUtils/feature-flags';


export type SingleOfferErrorType = {
  type: OfferNodeType.OFFER;
  offerError?: string | undefined;
  show?: boolean;
  hasValidOffer: boolean;
  hasError?: boolean;
};

export const ManageOfferWarning: React.FC = () => (
  <div className="tw-rounded-md tw-border-l-4 tw-border-cbryellow-500 tw-bg-cbryellow-100 tw-p-4">
    <p className="tw-text-sm tw-text-cbryellow-700">
      Manage offers is disabled. Enable this within the billing settings
      <span className="tw-font-semibold">
        {' '}
        (Settings &gt; Setup &gt; Configuration &gt; Billing system)
      </span>
      .
    </p>
  </div>
);

export const SingleOffer = () => {
  const {
    play,
    getPlayObjectProperty,
    setPlayObjectProperty,
    initializeTargetingNode,
    resetOfferNode,
    offerOptions,
    playValidationErrors,
    setPlayValidationErrors,
    handleCreateNewOffer,
    getDropdownTitle,
    getActiveTarget,
    handleEditOffer,
    handleCrossObjectiveOfferSelect,
    allOffers,
    showManageOfferWarning,
  } = useContext(PlayContext);
  const offerPresentation = getPlayObjectProperty(
    PlayObjectProperty.PRESENTATION
  );
  const playType = getPlayObjectProperty(PlayObjectProperty.PLAY_TYPE);

  const activeTarget = getActiveTarget();
  const appKey = useSelector(selectAppId);
  const anchorRef = useRef(null);
  const [openDropdown, setOpenDropdown] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const isMultiBranded = useFeatureFlags(
    FeatureFlagsE.ADDITIONAL_BRANDS_ENABLED
  );

  const { isLoading: isOffersLoading, isFetching: isOfferFetching } =
    offersAndLossAversionsSlice.endpoints.getOffers.useQuery({
      appKey,
      context: BBKRequestContext.lifecycle_reports,
    });

  const appMiscState = useSelector(selectAppMisc(appKey));

  const setAddNewOfferPath = (value?: any) => {
    dispatch(
      appPersistSlice.actions.saveAppMisc({
        appKey,
        playState: {
          play,
          addNewOffer: {
            path: ['draft_targeting', 'routing_percents', 1, 'target'],
            value: value,
          },
        },
      })
    );
  };
  const controlPercent = getPlayObjectProperty(
    PlayObjectProperty.CONTROL_PERCENTAGE
  ) as number;
  const offerNode = getPlayObjectProperty(
    PlayObjectProperty.OFFER_NODE
  ) as PercentToTargetType;
  const offerId = useMemo(() => {
    const offerTarget = _.get(offerNode, 'target', undefined);
    if (offerTarget === undefined) {
      return undefined;
    }
    return offerTarget.target_key;
  }, [offerNode]);

  const manageOfferWarning = showManageOfferWarning(offerId);

  const showAddControl =
    controlPercent === 0 &&
    getPlayObjectProperty(PlayObjectProperty.CONTROL_TARGET) === 'none';

  const validationErrorPath = [PlayValidationErrorType.Offer];
  const singleOfferValidationError = useMemo(() => {
    const errorNode = _.get(playValidationErrors, validationErrorPath);
    if (errorNode?.type === OfferNodeType.OFFER) {
      return errorNode;
    }
    return undefined;
  }, [playValidationErrors]);

  useEffect(() => {
    if (_.isEmpty(offerId)) {
      setPlayValidationErrors((prev) => {
        const updatedValidationErrors = produce(prev, (draft) => {
          _.set(draft, validationErrorPath, {
            type: OfferNodeType.OFFER,
            offerError: 'Select a valid offer',
            show: false,
            hasValidOffer: false,
            hasError: true,
          });
        });
        return updatedValidationErrors;
      });
    } else {
      setPlayValidationErrors((prev) => {
        const updatedValidationErrors = produce(prev, (draft) => {
          _.set(draft, validationErrorPath, {
            type: OfferNodeType.OFFER,
            hasValidOffer: true,
          });
        });
        return updatedValidationErrors;
      });
    }
  }, [offerId]);

  const createRoutingToTargetPercentNode = (target: {
    type: string;
    target_key: string;
  }): PercentToTargetType => ({
    type: RoutingPercentEnum.routingToTargetPercent,
    name: generateNewIdInt('pct_target'),
    percent: 100 - controlPercent,
    target,
  });

  const handleOfferChange = (offerKey: string) => {
    const node = createRoutingToTargetPercentNode({
      type: activeTarget,
      target_key: offerKey,
    });
    setPlayObjectProperty(PlayObjectProperty.OFFER_NODE, node);
  };

  const offerValue = useMemo(
    () => offerOptions.find((option) => option.value === offerId),
    [offerOptions, offerId]
  );

  const enhancedOfferSelect = useEnhancedOfferSelect({
    offers: allOffers,
    playType: playType as any,
    offerPresentation,
    isMultiBranded,
    onOfferSelect: handleOfferChange,
    onCreateNewOffer: () => {
      setAddNewOfferPath();
      handleCreateNewOffer();
    },
    onCrossObjectiveOfferSelect: handleCrossObjectiveOfferSelect,
  });

  const handleConvertToABTest = () => {
    initializeTargetingNode(OfferNodeType.TEST);
    const playState = appMiscState.playState;
    dispatch(
      appPersistSlice.actions.saveAppMisc({
        appKey,
        playState: {
          play: playState?.play,
          addNewOffer: {
            path: 'TO_AB_TEST',
            value: { target_key: offerId, type: activeTarget },
          },
        },
      })
    );
  };

  const title = (
    <div>
      <span className="tw-mr-2">Offer</span>
      <button ref={anchorRef} onClick={() => setOpenDropdown(true)}>
        {icons.threeDots}
      </button>
      <Popover
        id="dropdown"
        open={openDropdown}
        anchorEl={anchorRef.current}
        onClose={() => setOpenDropdown(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <div>
          {offerPresentation && (
            <button
              className="tw-block tw-w-full tw-px-3 tw-py-2 tw-text-left hover:tw-bg-[#E1F1F9]"
              onClick={handleConvertToABTest}
            >
              <span className="tw-mr-1">{icons.percent}</span>
              Convert to Test
            </button>
          )}
          <button
            className="tw-block tw-w-full tw-px-3 tw-py-2 tw-text-left tw-text-cbrdanger-400 hover:tw-bg-[#E1F1F9]"
            onClick={resetOfferNode}
          >
            <span className="tw-mr-1">{icons.trash1}</span>
            Remove
          </button>
        </div>
      </Popover>
    </div>
  );

  const actions: Action[] = [
    {
      icon: ActionMenuIcons.pencil,
      copy: 'Edit',
      onClick: () => {
        handleEditOffer(offerId);
        setAddNewOfferPath();
      },
    },
    {
      icon: ActionMenuIcons.duplicate,
      copy: 'Copy',
      onClick: () => {
        handleEditOffer(offerId, true);
        setAddNewOfferPath();
      },
    },
  ];

  const content = (
    <DisableContainer editableAfterLive>
      <CollapsableForm
        title={title}
        id={3}
        collapseElement={<div className="tw-min-w-[45rem]" />}
      >
        {offerPresentation === undefined ? (
          <OfferPresentationMode />
        ) : (
          <div className="tw-mb-5 tw-mt-4 tw-min-w-[45rem] tw-rounded-sm tw-border tw-border-[#5CB5E5] tw-bg-white tw-px-6 tw-py-3">
            <div className="tw-flex tw-flex-col">
              <div className="tw-flex tw-items-center">
                <span className="tw-mr-2 tw-font-semibold">Widget:</span>
                <OfferPresentationModeDropdown />
                {showAddControl && <AddControlButton />}
              </div>
              {showAddControl && (
                <div className="tw-my-5 tw-w-full tw-border-b tw-border-solid tw-border-[#DDDFE2] tw-px-10" />
              )}
              {!showAddControl && <PlayControlSelect componentTitle="Offer" />}
              <div className="tw-flex tw-w-2/3 tw-flex-col">
                <span className="tw-pb-2 tw-font-semibold">
                  {getDropdownTitle()}
                  <span className="tw-w-5">
                    {(isOffersLoading || isOfferFetching) && icons.loaderSW6}
                  </span>
                </span>
                <div className="tw-flex tw-flex-row tw-items-center">
                  <GenericSelect
                    className="tw-mr-2 tw-w-full"
                    options={
                      enhancedOfferSelect?.groupedOptions || offerOptions
                    }
                    value={offerValue ?? null}
                    onChange={(e) => {
                      if (enhancedOfferSelect?.onSelect) {
                        const crossObjectiveOffer = enhancedOfferSelect.categorization?.nonMatchingOffers.find(
                          (categorizedOffer: any) => categorizedOffer.offer.id === e.value
                        );
                        if (crossObjectiveOffer) {
                          setAddNewOfferPath(crossObjectiveOffer.offer);
                          handleCrossObjectiveOfferSelect(crossObjectiveOffer.offer);
                          return;
                        }
                        enhancedOfferSelect.onSelect(e.value);
                      } else {
                        if (e.value === ADD_OFFER_VALUE) {
                          setAddNewOfferPath();
                          handleCreateNewOffer();
                          return;
                        }
                        handleOfferChange(e.value);
                      }
                    }}
                    key={offerValue?.value}
                  />
                  {offerId && <GenericActionMenu actions={actions} />}
                  {manageOfferWarning && (
                    <TippyTooltip
                      tippyOptions={{
                        placement: 'right',
                      }}
                      content={<ManageOfferWarning />}
                    >
                      <span className="tw-ml-4 tw-text-xs tw-text-[#FFB300] [&_svg]:tw-h-5 [&_svg]:tw-w-5">
                        {icons.warning}
                      </span>
                    </TippyTooltip>
                  )}
                </div>

                <span className="tw-pl-1 tw-text-xs tw-text-danger-500">
                  {singleOfferValidationError &&
                    singleOfferValidationError?.show &&
                    singleOfferValidationError.offerError}
                </span>
              </div>
            </div>
          </div>
        )}
      </CollapsableForm>
    </DisableContainer>
  );
  return (
    <div className="tw-flex tw-border tw-border-transparent">
      <div className="tw-flex tw-flex-col tw-items-center">
        <div className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-[#5CB5E5] tw-bg-[#C9ECFF]">
          {icons.gift}
        </div>
      </div>
      <div className="tw-py-2 tw-pl-5">{content}</div>
    </div>
  );
};
